import { useMutation as useMarkAsReadMutation } from "@faire/web-api--source/endpoints/www/api/v3/messenger/read-conversation/post-hooks";
import { useDebouncedByToken } from "@faire/web/common/useDebouncedByToken";
import { useCallback } from "react";

import {
  ConversationModel,
  updateConversation,
  isConversationUnRead,
} from "./ConversationModel";

/**
 * Hook for marking a conversation as read with debounced deduplication
 * Prevents burst of multiple API calls for the same token using lodash debounce
 */
export const useMarkAsRead = () => {
  const { mutateAsync: markAsReadMutation } = useMarkAsReadMutation();

  const debouncedMarkAsRead = useDebouncedByToken(
    async (conversation: ConversationModel) => {
      const token = conversation.data.token!;
      const response = await markAsReadMutation([
        {
          conversation_token: token,
        },
      ]);
      return response;
    },
    (conversation: ConversationModel) => conversation.data.token ?? "",
    1000,
    {
      leading: true,
      trailing: true,
    }
  );

  return useCallback(
    async (
      conversation: ConversationModel
    ): Promise<[ConversationModel, number | undefined]> => {
      const token = conversation.data.token!;

      // Early return if conversation has no unread messages
      if (!token || !isConversationUnRead(conversation)) {
        return [conversation, undefined];
      }

      const response = await debouncedMarkAsRead(conversation);

      if (response?.conversation) {
        return [
          updateConversation(conversation, response.conversation),
          response.total_unread_conversations,
        ];
      }

      return [conversation, response?.total_unread_conversations];
    },
    [debouncedMarkAsRead]
  );
};
